import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import React from 'react'

const visibleImage = new URL('../../images/visible.png', import.meta.url).href
import { colors } from './KestrixDesignSystem.jsx'

/**
 * Canonical thermal palette gradient stops, expressed as ordered
 * [color, percentage] pairs. Stops go from low temperature (0%) to
 * high temperature (100%), so they can be rendered horizontally OR
 * vertically by callers without duplicating the colour definitions.
 *
 * Exported so other components (e.g. the 3D viewer thermal scale legend)
 * can stay visually in sync with the toolbar gradient buttons.
 */
export const THERMAL_GRADIENT_STOPS = {
  IRON: [
    ['#240A2E', 0],
    ['#76008C', 5.05],
    ['#A900A1', 16.34],
    ['#C7342A', 45.58],
    ['#CD6800', 69.15],
    ['#DDB101', 89.62],
    ['#FFFDF5', 97.89]
  ],
  RAINBOW_HC: [
    ['#000000', 0],
    ['#DB00AA', 12],
    ['#0000FF', 25],
    ['#00EDD8', 38],
    ['#00460A', 50],
    ['#FFFF00', 62],
    ['#640000', 75],
    ['#C80000', 88],
    ['#FFFFFF', 100]
  ],
  Thermal: [
    ['#000004', 0],
    ['#3B0F70', 25],
    ['#8C2981', 50],
    ['#DE4968', 70],
    ['#FE9F6D', 85],
    ['#FCFDBF', 100]
  ],
  BLACK_HOT: [
    ['#000004', 0],
    ['rgb(24, 24, 24)', 25],
    ['rgb(48, 48, 48)', 50],
    ['rgb(108, 108, 108)', 70],
    ['rgb(173, 173, 173)', 85],
    ['rgb(192, 192, 192)', 100]
  ]
}

/**
 * Build a CSS linear-gradient string from a stops array.
 *
 * @param {Array<[string, number]>} stops - Array of [color, percentage] pairs.
 * @param {string} [direction='90deg'] - CSS gradient direction (e.g. '90deg', 'to top').
 * @returns {string} A CSS `linear-gradient(...)` value.
 */
export const buildThermalGradient = (stops, direction = '90deg') =>
  `linear-gradient(${direction}, ${stops.map(([color, pct]) => `${color} ${pct}%`).join(', ')})`

/**
 * Reusable thermal gradient button component
 * Used for thermal visualization controls in 3D viewers, maps, and other thermal interfaces
 */
const KestrixThermalGradient = ({
  variant = 'IRON', // 'IRON', 'RAINBOW_HC', 'Thermal', 'Visible'
  isSelected = false,
  onClick,
  size = 'small', // 'small', 'medium', 'large'
  tooltip,
  disabled = false,
  ...props
}) => {
  // Size configurations
  const sizeConfig = {
    small: {
      width: '24px',
      height: '24px',
      borderRadius: '6px'
    },
    medium: {
      width: '30px',
      height: '30px',
      borderRadius: '6px'
    },
    large: {
      width: '36px',
      height: '36px',
      borderRadius: '8px'
    }
  }

  // Gradient configurations - colour stops sourced from THERMAL_GRADIENT_STOPS so
  // any palette tweaks stay in sync with the 3D viewer thermal scale legend.
  const gradientConfig = {
    IRON: {
      gradient: buildThermalGradient(THERMAL_GRADIENT_STOPS.IRON, '90deg'),
      borderColor: colors.base.KestrixThermalMode,
      tooltip: 'Iron',
      description: 'Iron thermal visualization'
    },
    RAINBOW_HC: {
      gradient: buildThermalGradient(
        THERMAL_GRADIENT_STOPS.RAINBOW_HC,
        '90deg'
      ),
      borderColor: colors.base.KestrixThermalMode,
      tooltip: 'Rainbow HC',
      description: 'High contrast rainbow thermal visualization'
    },
    thermal: {
      gradient: buildThermalGradient(THERMAL_GRADIENT_STOPS.Thermal, '90deg'),
      borderColor: colors.base.KestrixThermalMode,
      tooltip: 'Thermal',
      description: 'Standard thermal visualization'
    },
    Visible: {
      borderColor: colors.base.KestrixThermalMode,
      tooltip: 'Visible',
      description: 'Visible light thermal visualization'
    },
    BLACK_HOT: {
      gradient: buildThermalGradient(THERMAL_GRADIENT_STOPS.BLACK_HOT, '90deg'),
      borderColor: colors.base.KestrixThermalMode,
      tooltip: 'BLACK_HOT',
      description: 'BLACK_HOT thermal visualization'
    }
  }

  const currentSize = sizeConfig[size]
  const currentGradient = gradientConfig[variant]
  const displayTooltip = tooltip || currentGradient?.tooltip

  const handleClick = (event) => {
    if (disabled) return
    if (onClick) onClick(event, variant)
  }

  const ButtonComponent = (
    <Box
      component="button"
      onClick={handleClick}
      disabled={disabled}
      sx={{
        background: 'transparent',
        border: 'none',
        borderRadius: 1,
        padding: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: disabled ? 0.5 : 1,
        filter: isSelected
          ? 'drop-shadow(0 2px 8px rgba(255, 138, 0, 0.25))'
          : 'none',
        transform: isSelected ? 'scale(1.05)' : 'scale(1)',
        '&:hover': !disabled
          ? {
              filter: 'drop-shadow(0 2px 8px rgba(72, 72, 72, 0.12))',
              transform: 'scale(1.02)'
            }
          : {},
        '&:focus': {
          outline: 'none'
        }
      }}
      {...props}
    >
      <Box
        sx={{
          width: currentSize.width,
          height: currentSize.height,
          background:
            variant === 'Visible'
              ? `url(${visibleImage})`
              : currentGradient.gradient,
          backgroundSize: variant === 'Visible' ? '100% 100%' : 'auto',
          backgroundPosition: variant === 'Visible' ? 'center' : 'auto',
          backgroundRepeat: variant === 'Visible' ? 'no-repeat' : 'auto',
          borderRadius: currentSize.borderRadius,
          border: isSelected
            ? `2px solid ${colors.base.KestrixThermalMode}`
            : '2px solid transparent',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': !disabled
            ? {
                border: isSelected
                  ? `2px solid ${colors.base.KestrixThermalMode}`
                  : '2px solid rgba(255, 138, 0, 0.3)'
              }
            : {}
        }}
      />
    </Box>
  )

  // Wrap with tooltip if provided
  if (displayTooltip) {
    return (
      <Tooltip
        title={displayTooltip}
        arrow
        placement="top"
        enterDelay={500}
        leaveDelay={0}
        PopperProps={{
          sx: {
            zIndex: 9999
          }
        }}
      >
        <div style={{ display: 'inline-block' }}>{ButtonComponent}</div>
      </Tooltip>
    )
  }

  return ButtonComponent
}

/**
 * Defines the standard order for gradient variant names (as used in KestrixThermalGradient)
 * Visible (RGB) first, then IRON, then RAINBOW_HC, then Thermal
 */
const GRADIENT_VARIANT_ORDER = ['Visible', 'IRON', 'RAINBOW_HC', 'thermal']

/**
 * Generic function to sort variants according to a standard order
 * @param {Array<string>} variants - Array of variant names to sort
 * @param {Array<string>} orderArray - Array defining the standard order
 * @returns {Array<string>} Sorted array of variants
 */
const sortVariantsByOrder = (variants, orderArray) => {
  return [...variants].sort((a, b) => {
    const indexA = orderArray.indexOf(a)
    const indexB = orderArray.indexOf(b)

    // If both are in the standard order, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB
    }
    // If only one is in the standard order, prioritize it
    if (indexA !== -1) return -1
    if (indexB !== -1) return 1
    // If neither is in the standard order, maintain original order
    return 0
  })
}

/**
 * Sorts gradient variants according to the standard order
 * @param {Array<string>} variants - Array of gradient variant names
 * @returns {Array<string>} Sorted array of variants
 */
const sortGradientVariantsByStandardOrder = (variants) => {
  return sortVariantsByOrder(variants, GRADIENT_VARIANT_ORDER)
}

/**
 * Thermal Gradient Group - for displaying multiple gradient options
 */
export const KestrixThermalGradientGroup = ({
  variants = ['IRON', 'RAINBOW_HC', 'Visible'],
  selectedVariant,
  onVariantChange,
  size = 'medium',
  gap = 1,
  orientation = 'horizontal', // 'horizontal' | 'vertical'
  disabled = false,
  ...props
}) => {
  // Sort variants to ensure consistent button order (Visible/RGB first, then IRON, then RAINBOW_HC, then Thermal)
  const sortedVariants = sortGradientVariantsByStandardOrder(variants)

  const containerStyles = {
    display: 'flex',
    flexDirection: orientation === 'vertical' ? 'column' : 'row',
    gap: `${gap * 4}px`,
    alignItems: 'center'
  }

  return (
    <div style={containerStyles} {...props}>
      {sortedVariants.map((variant) => (
        <KestrixThermalGradient
          key={variant}
          variant={variant}
          isSelected={selectedVariant === variant}
          onClick={(event, clickedVariant) =>
            onVariantChange && onVariantChange(clickedVariant)
          }
          size={size}
          disabled={disabled}
        />
      ))}
    </div>
  )
}

/**
 * Helper function to create custom gradient configurations
 */
export const createThermalGradientConfig = (
  name,
  gradient,
  borderColor,
  tooltip,
  description
) => ({
  [name]: {
    gradient,
    borderColor,
    tooltip,
    description
  }
})

/**
 * Predefined gradient sets for common use cases
 */
export const thermalGradientSets = {
  // Basic set - most common thermal gradients
  basic: ['IRON', 'RAINBOW_HC', 'Visible'],

  // Extended set - includes all variants
  extended: ['IRON', 'RAINBOW_HC', 'Thermal', 'Visible'],

  // Temperature focused
  temperature: ['IRON', 'RAINBOW_HC'],

  // Scientific set - for research applications
  scientific: ['IRON', 'RAINBOW_HC', 'Visible'],

  // Thermal/RGB group - for models with thermal and RGB variants only
  thermalRgb: ['Thermal', 'Visible']
}

/**
 * Variant mapping for toolbar gradient component rendering (includes additional metadata)
 */
const VARIANT_TO_GRADIENT_MAPPING = {
  IRON: { gradientVariant: 'IRON', toolName: 'thermal_iron', tooltip: 'Iron' },
  RAINBOW_HC: {
    gradientVariant: 'RAINBOW_HC',
    toolName: 'thermal_rainbow_hc',
    tooltip: 'Rainbow HC'
  },
  Thermal: {
    gradientVariant: 'thermal',
    toolName: 'thermal_thermal',
    tooltip: 'Thermal'
  },
  RGB: {
    gradientVariant: 'Visible',
    toolName: 'thermal_visible',
    tooltip: 'Visible'
  }
}

/**
 * Defines the standard order for gradient buttons in toolbar
 * RGB first, then IRON, then RAINBOW_HC, then Thermal
 */
const TOOLBAR_GRADIENT_BUTTON_ORDER = ['RGB', 'IRON', 'RAINBOW_HC', 'Thermal']

/**
 * Sorts toolbar variants according to the standard gradient button order
 * @param {Array<string>} variants - Array of toolbar variant names (RGB, IRON, etc.)
 * @returns {Array<string>} Sorted array of variants
 */
const sortToolbarVariantsByStandardOrder = (variants) => {
  return sortVariantsByOrder(variants, TOOLBAR_GRADIENT_BUTTON_ORDER)
}

/**
 * Thermal Gradient Buttons Component for Toolbar
 * Renders a group of thermal gradient buttons with consistent ordering for use in toolbars
 * Maps toolbar variant names (RGB, IRON, etc.) to gradient variants and tool names
 *
 * @param {Object} props
 * @param {Array<string>} props.allowedVariants - Array of allowed variant names (e.g., ['RGB', 'IRON', 'RAINBOW_HC'])
 * @param {string} props.selectedTool - Currently selected tool name (e.g., 'thermal_iron')
 * @param {Function} props.setSelectedTool - Function to set the selected tool
 */
export const KestrixThermalGradientButtons = ({
  allowedVariants = [],
  selectedTool,
  setSelectedTool
}) => {
  // Sort variants to ensure consistent button order (RGB first, then IRON, then RAINBOW_HC, then Thermal)
  const sortedVariants = sortToolbarVariantsByStandardOrder(allowedVariants)

  // Filter and map allowed variants to gradient configurations using shared mapping
  const gradientButtons = sortedVariants
    .map((variant) => {
      // Get the mapping (exact match)
      const mapping = VARIANT_TO_GRADIENT_MAPPING[variant]

      if (!mapping) return null

      return {
        variant,
        ...mapping
      }
    })
    .filter((btn) => btn !== null) // Remove null entries

  return (
    <>
      {gradientButtons.map(
        ({ variant, gradientVariant, toolName, tooltip }) => (
          <KestrixThermalGradient
            key={variant}
            variant={gradientVariant}
            isSelected={selectedTool === toolName}
            onClick={() => {
              setSelectedTool(toolName)
            }}
            size="small"
            tooltip={tooltip}
          />
        )
      )}

      {/* Divider */}
      {gradientButtons.length > 0 && (
        <div
          style={{
            width: '2px',
            height: '24px',
            backgroundColor: colors.border.medium
          }}
        ></div>
      )}
    </>
  )
}

export default KestrixThermalGradient
